<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { debounce } from 'lodash'
import { useHttp, useTranslate } from '@/shared/lib'
import { VSelectSearch } from '@/shared/ui'
import {
  NULL_SELECT_TITLE,
  NULL_SELECT_VALUE,
} from '@/entities/Inventory/ObjectSubTypes/model/helpers'
import { buildLinkSelectMenuProps, linkSelectMenuPropsKey } from '../../model/linkSelectMenuProps'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  object_type?: number | string
  field?: { id: string }
  label?: string
  disabled?: boolean
  readonly?: boolean
  hideDetails?: boolean
  mandatory?: boolean
  showMandatoryError?: boolean
}>()

/** Сырое значение поля — object_list_id выбранного объекта либо null. */
const value = defineModel<string | number | null>({ default: null })

const updateParentField = inject<(fieldId: string, value: string) => void>(
  'updateLinkedFieldValue',
  () => {},
)
/** Модалка выбора подписывается на название выбранного объекта (apply label). */
const updateParentFieldLabel = inject<(fieldId: string, label: string) => void>(
  'updateLinkedFieldLabel',
  () => {},
)
const parentMenuProps = inject(linkSelectMenuPropsKey, null)
const menuProps = computed(() => buildLinkSelectMenuProps(parentMenuProps))

const route = useRoute()
const http = useHttp()
const { trs } = useTranslate()

const items = ref<{ value: string; title: string }[]>([])
const selectedName = ref('')
const loading = ref(false)

const objectType = computed(() => Number(props.object_type) || 0)
const groupId = computed(() => String(route.params.object_id ?? ''))

/**
 * Опции для селекта: первой всегда опцию «пусто», а выбранное значение
 * держим на вершине, чтобы оно не исчезло при серверном поиске.
 */
const displayItems = computed(() => {
  const list = [...items.value]
  if (
    value.value != null &&
    value.value !== '' &&
    !list.some((it) => String(it.value) === String(value.value)) &&
    selectedName.value
  ) {
    list.unshift({ value: String(value.value), title: selectedName.value })
  }
  return [{ value: NULL_SELECT_VALUE, title: NULL_SELECT_TITLE }, ...list]
})

/** Опция «пусто» и текущий выбор не должны пропадать при поиске. */
const preserveValues = computed(() => {
  const v = value.value
  return v == null || v === '' ? [NULL_SELECT_VALUE] : [NULL_SELECT_VALUE, String(v)]
})

/**
 * Модель селекта: пустое значение показываем опцией «пусто»,
 * но в поле сохраняем null.
 */
const selectModel = computed({
  get: () => {
    const v = value.value
    return v == null || v === '' ? NULL_SELECT_VALUE : v
  },
  set: (next) => {
    value.value = next === NULL_SELECT_VALUE ? null : next
  },
})

/** Загрузка кандидатов с сервера (опционально с поисковым термином). */
async function load(search = '') {
  if (!groupId.value || !objectType.value) return
  loading.value = true
  try {
    const params = new URLSearchParams({ page: '1', per_page: '50' })
    if (search) params.set('search', search)
    const url = `/inventory/objects/${groupId.value}/inventory-types/${objectType.value}/elements?${params}`
    const res = await http.get(url)
    const rows = (res.data ?? []) as any[]
    items.value = rows.map((row: any) => ({
      value: String(row.object_list_id),
      title: String(row.name ?? ''),
    }))
    if (value.value) {
      const found = items.value.some((it) => String(it.value) === String(value.value))
      if (!found) fetchSelectedName(value.value)
    }
  } finally {
    loading.value = false
  }
}

/** Дебаунс поиска: подгружаем элементы спустя 300 мс после ввода. */
const debouncedLoad = debounce((term: string) => {
  void load(term)
}, 300)

/** Достаёт название выбранного объекта, если оно не попало в загруженный список. */
async function fetchSelectedName(id: string | number) {
  if (id === null || id === undefined || id === '') return
  const alreadyInItems = items.value.some((it) => String(it.value) === String(id))
  if (alreadyInItems) return
  try {
    const params = new URLSearchParams({ object_ids: String(id) })
    const res = await http.get(`/inventory/objects/passport?${params}`)
    const rows = (res.data ?? []) as any[]
    const row = rows[0]
    if (row) {
      selectedName.value = String(row.name ?? trs('itf.unknown_object'))
    }
  } catch {
    /* ignore */
  }
}

onMounted(() => {
  load()
  if (value.value) fetchSelectedName(value.value)
})

watch(
  () => objectType.value,
  () => load(),
)

watch(value, (v) => {
  if (v !== null && v !== undefined && v !== '') {
    fetchSelectedName(v)
    if (props.field?.id) updateParentField(props.field.id, String(v))
  }
})

/** Название текущего выбора — из загруженных опций либо из догруженного selectedName. */
function currentSelectionTitle(): string {
  if (value.value == null || value.value === '') return ''
  const found = items.value.find((it) => String(it.value) === String(value.value))
  return (found?.title ?? selectedName.value ?? '').trim()
}

watch([value, items, selectedName], () => {
  const title = currentSelectionTitle()
  if (title && props.field?.id) updateParentFieldLabel(String(props.field.id), title)
})

onBeforeUnmount(() => debouncedLoad.cancel())
</script>

<template>
  <VSelectSearch
    v-model="selectModel"
    server-side
    class="mb-3"
    :items="displayItems"
    :loading="loading"
    :disabled="disabled || !objectType"
    :label="label"
    :hide-details="hideDetails"
    :readonly="readonly"
    :error="showMandatoryError ? true : undefined"
    :preserve-values="preserveValues"
    :menu-props="menuProps"
    item-title="title"
    item-value="value"
    @update:search="debouncedLoad"
  />
</template>
